import * as React from 'react';
import { MoreHorizontal, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type TableRowAction = {
  key: string;
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  variant?: 'default' | 'destructive';
  /** When false, action is omitted. Undefined/true = show. */
  permission?: boolean;
  /** Candidate for the visible primary strip. */
  primary?: boolean;
  /** Prefer a compact labeled button over an icon in the primary strip. */
  labeled?: boolean;
  /** Class for the label text of a labeled action; callers may collapse it to the icon at narrow widths (aria-label keeps the name). */
  labelClassName?: string;
  disabled?: boolean;
  /**
   * POS-I244. Plain, visible one-line reason shown under a disabled labeled action (and linked with
   * aria-describedby), so nobody has to hover to learn why. Ignored when the action is enabled.
   */
  disabledReason?: string;
};

export type TableRowActionsProps = {
  actions: TableRowAction[];
  /** Max visible primary actions. Default 2. */
  maxPrimary?: number;
  className?: string;
  moreTooltip?: string;
};

function isAllowed(action: TableRowAction): boolean {
  return action.permission !== false;
}

function partitionActions(
  actions: TableRowAction[],
  maxPrimary: number,
): { primary: TableRowAction[]; menu: TableRowAction[] } {
  const allowed = actions.filter(isAllowed);
  if (allowed.length === 0) return { primary: [], menu: [] };

  if (allowed.length === 1) {
    return { primary: allowed, menu: [] };
  }

  const destructive = allowed.filter((a) => a.variant === 'destructive');
  const rest = allowed.filter((a) => a.variant !== 'destructive');

  const flagged = rest.filter((a) => a.primary);
  const unflagged = rest.filter((a) => !a.primary);
  const primaryStrip = [...flagged, ...unflagged].slice(0, maxPrimary);
  const primaryKeys = new Set(primaryStrip.map((a) => a.key));
  const menu = [...rest.filter((a) => !primaryKeys.has(a.key)), ...destructive];

  return { primary: primaryStrip, menu };
}

/**
 * Listing row actions: ≤2 primary (icon or compact label) + overflow “More”.
 * Unauthorized actions are omitted (never disabled-for-permission).
 */
export function TableRowActions({
  actions,
  maxPrimary = 2,
  className,
  moreTooltip,
}: TableRowActionsProps) {
  const { t } = useTranslation('common');
  const baseId = React.useId();
  const { primary, menu } = React.useMemo(
    () => partitionActions(actions, maxPrimary),
    [actions, maxPrimary],
  );

  if (primary.length === 0 && menu.length === 0) return null;

  const moreLabel = moreTooltip ?? t('moreActions', { defaultValue: 'More actions' });

  const menuDefault = menu.filter((a) => a.variant !== 'destructive');
  const menuDestructive = menu.filter((a) => a.variant === 'destructive');

  return (
    <div className={cn('flex items-center justify-end gap-1', className)}>
      {primary.map((action) => {
        const Icon = action.icon;
        if (action.labeled) {
          const reasonId = `${baseId}-${action.key}-why`;
          const showReason = !!action.disabled && !!action.disabledReason;
          const button = (
            <Button
              key={action.key}
              type="button"
              variant="ghost"
              size="sm"
              disabled={action.disabled}
              aria-label={action.labelClassName ? action.label : undefined}
              aria-describedby={showReason ? reasonId : undefined}
              onClick={action.onSelect}
              className={cn(
                'h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground',
                action.variant === 'destructive' && 'hover:text-destructive-text',
              )}
            >
              {Icon ? <Icon className="h-4 w-4" /> : null}
              {action.labelClassName ? <span className={action.labelClassName}>{action.label}</span> : action.label}
            </Button>
          );
          if (!showReason) return button;
          return (
            <div key={action.key} className="flex flex-col items-end gap-0.5">
              {button}
              <span id={reasonId} className="max-w-[16rem] text-end text-xs text-muted-foreground whitespace-normal">
                {action.disabledReason}
              </span>
            </div>
          );
        }
        return (
          <IconButton
            key={action.key}
            variant="ghost"
            className={cn(
              'h-8 w-8 text-muted-foreground hover:text-foreground',
              action.variant === 'destructive' && 'hover:text-destructive-text',
            )}
            onClick={action.onSelect}
            tooltip={action.label}
            disabled={action.disabled}
          >
            {Icon ? <Icon className="h-4 w-4" /> : null}
          </IconButton>
        );
      })}

      {menu.length > 0 && (
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  aria-label={moreLabel}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">{moreLabel}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end">
            {menuDefault.map((action) => {
              const Icon = action.icon;
              return (
                <DropdownMenuItem
                  key={action.key}
                  disabled={action.disabled}
                  onSelect={() => action.onSelect()}
                >
                  {Icon ? <Icon /> : null}
                  {action.label}
                </DropdownMenuItem>
              );
            })}
            {menuDestructive.length > 0 && menuDefault.length > 0 && (
              <DropdownMenuSeparator />
            )}
            {menuDestructive.map((action) => {
              const Icon = action.icon;
              return (
                <DropdownMenuItem
                  key={action.key}
                  disabled={action.disabled}
                  className="text-destructive-text focus:text-destructive-text data-[highlighted]:text-destructive-text [&_svg]:text-destructive-text"
                  onSelect={() => action.onSelect()}
                >
                  {Icon ? <Icon /> : null}
                  {action.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
